<template>
  <div :class="isSubmitting && 'pointer-events-none opacity-60'">
    <div class="mb-5">
      <FieldSelect
        id="form_support_type"
        v-model="form.issueType"
        :name="t('issue-type')"
        :is-error="!!formErrorMessage.issueType"
        :error-message="formErrorMessage.issueType"
        required
        @change="technicalSupportStore.VALID_FORM_ISSUE_TYPE"
      >
        <option value="" disabled>
          {{ $t('form.select') }}
        </option>
        <option v-for="item in issueTypes" :key="item.code" :value="item.code">
          {{ item.name }}
        </option>
      </FieldSelect>
    </div>
    <div class="mb-5">
      <FieldInputText
        id="form_related-product-name"
        v-model="form.relatedProductName"
        :name="t('related-product-name')"
        :is-error="!!formErrorMessage.relatedProductName"
        :error-message="formErrorMessage.relatedProductName"
        :maxlength="100"
        @blur="technicalSupportStore.VALID_FORM_RELATED_PRODUCT_NAME"
        @change="technicalSupportStore.VALID_FORM_RELATED_PRODUCT_NAME"
      />
    </div>
    <div class="tablet:flex tablet:items-start">
      <div class="mb-5 tablet:w-1/2 tablet:pr-2">
        <FieldInputText
          id="form_first-name"
          v-model="form.firstName"
          :name="$t('form.first-name')"
          :is-error="!!formErrorMessage.firstName"
          :error-message="formErrorMessage.firstName"
          :maxlength="100"
          required
          @blur="technicalSupportStore.VALID_FORM_FIRST_NAME"
          @change="technicalSupportStore.VALID_FORM_FIRST_NAME"
        />
      </div>
      <div class="mb-5 tablet:w-1/2 tablet:pl-2">
        <FieldInputText
          id="form_last-name"
          v-model="form.lastName"
          :name="$t('form.last-name')"
          :is-error="!!formErrorMessage.lastName"
          :error-message="formErrorMessage.lastName"
          :maxlength="100"
          required
          @blur="technicalSupportStore.VALID_FORM_LAST_NAME"
          @change="technicalSupportStore.VALID_FORM_LAST_NAME"
        />
      </div>
    </div>
    <div class="tablet:flex tablet:items-start">
      <div class="mb-5 tablet:w-1/2 tablet:pr-2">
        <FieldInputText
          id="form_email"
          v-model="form.email"
          :name="$t('form.email')"
          :is-error="!!formErrorMessage.email"
          :error-message="formErrorMessage.email"
          type="email"
          placeholder="example@example.com"
          required
          @blur="technicalSupportStore.VALID_FORM_EMAIL"
          @change="technicalSupportStore.VALID_FORM_EMAIL"
        />
      </div>
      <div class="mb-5 tablet:w-1/2 tablet:pl-2">
        <FieldInputPhone
          id="form_phone"
          v-model:phone-code="form.phoneCode"
          v-model:phone-number="form.phoneNumber"
          :name="$t('form.phone-number')"
          :is-error="
            !!formErrorMessage.phoneCode || !!formErrorMessage.phoneNumber
          "
          :error-message="
            formErrorMessage.phoneCode || formErrorMessage.phoneNumber
          "
          :placeholder="$t('form.phone-number-placeholder')"
          required
          @code-blur="technicalSupportStore.VALID_FORM_PHONE_CODE"
          @code-change="technicalSupportStore.VALID_FORM_PHONE_CODE"
          @number-blur="technicalSupportStore.VALID_FORM_PHONE_NUMBER"
          @number-change="technicalSupportStore.VALID_FORM_PHONE_NUMBER"
        />
      </div>
    </div>
    <div class="tablet:flex tablet:items-start">
      <div class="mb-5 tablet:w-1/2 tablet:pr-2">
        <FieldInputText
          id="form_company-name"
          v-model="form.companyName"
          :name="$t('form.company-name')"
          :is-error="!!formErrorMessage.companyName"
          :error-message="formErrorMessage.companyName"
          :maxlength="100"
          required
          @blur="technicalSupportStore.VALID_FORM_COMPANY_NAME"
          @change="technicalSupportStore.VALID_FORM_COMPANY_NAME"
        />
      </div>
      <div class="mb-5 tablet:w-1/2 tablet:pl-2">
        <FieldSelect
          id="form_country"
          v-model="form.country"
          :name="$t('form.country-region')"
          :is-error="!!formErrorMessage.country"
          :error-message="formErrorMessage.country"
          required
          @change="technicalSupportStore.VALID_FORM_COUNTRY"
        >
          <option value="" disabled>
            {{ $t('form.select') }}
          </option>
          <option v-for="item in countries" :key="item" :value="item">
            {{ item }}
          </option>
        </FieldSelect>
      </div>
    </div>
    <div class="mb-5">
      <FieldInputTextArea
        id="form_message"
        v-model="form.message"
        :name="$t('form.message')"
        :is-error="!!formErrorMessage.message"
        :error-message="formErrorMessage.message"
        :maxlength="500"
        required
        @blur="technicalSupportStore.VALID_FORM_MESSAGE"
        @change="technicalSupportStore.VALID_FORM_MESSAGE"
      />
    </div>
    <div class="mb-5">
      <FieldUploadImage
        id="form_attachment"
        v-model="form.attachment"
        :name="t('attachment')"
      />
    </div>

    <FormPrivacy
      :props-privacy="privacy"
      :country-name="form.country"
      @update="handleUpdatePrivacy"
      @valid="handleValidPrivacy"
    />

    <FormCaptcha
      class="mb-5"
      @on-verify="verifyCaptcha"
      @on-expire="expireCaptcha"
    />

    <div class="flex flex-col gap-3 tablet:flex-row tablet:gap-4">
      <TheButton
        class="flex w-full items-center justify-center tablet:w-1/2"
        :title="$t('form.submit')"
        @click="handleSubmit"
      >
        <span class="text-sm leading-6">
          {{ $t('form.submit') }}
        </span>
        <IconLoader v-if="isSubmitting" class="ml-1 h-4 w-4 text-white" />
      </TheButton>
      <TheButton
        type="outline"
        :to="`/${locale}/marketplace/help/contact-us`"
        :title="$t('form.back')"
        class="w-full tablet:w-1/2"
      >
        <span class="text-sm">
          {{ $t('form.back') }}
        </span>
      </TheButton>
    </div>
    <p class="mt-3 text-center text-sm text-gray-600">
      {{ $t('form.privacy-policy.first-part') }}
      <TheNuxtLink
        :to="privacyPolicyUrl"
        :title="$t('form.privacy-policy.policy')"
        class="text-blue-400 hover:text-blue-500 hover:underline"
      >
        {{ $t('form.privacy-policy.policy') }}
      </TheNuxtLink>
      {{ $t('form.privacy-policy.second-part') }}
    </p>
    <p v-if="!isCN" class="text-center text-sm text-gray-600">
      {{ $t('form.privacy-policy.recaptcha-first-part') }}
      <TheNuxtLink
        class="text-blue-400 hover:text-blue-500 hover:underline"
        :title="$t('form.privacy-policy.policy')"
        to="https://policies.google.com/privacy"
      >
        {{ $t('form.privacy-policy.policy') }}
      </TheNuxtLink>
      {{ $t('form.privacy-policy.recaptcha-second-part') }}
      <TheNuxtLink
        class="text-blue-400 hover:text-blue-500 hover:underline"
        :title="$t('form.privacy-policy.service')"
        to="https://policies.google.com/terms"
      >
        {{ $t('form.privacy-policy.service') }}
      </TheNuxtLink>
      {{ $t('form.privacy-policy.recaptcha-third-part') }}
    </p>
  </div>
</template>

<script setup lang="ts">
  import type { IFormPrivacy } from '@/components/FormPrivacy.vue';

  const { query } = useRoute();
  const { t, locale } = useI18n({ useScope: 'local' });
  const mainStore = useMainStore();
  const profileStore = useProfileStore();
  const technicalSupportStore = useTechnicalSupportStore();
  const { countries, isShowFormWarningPopUp, isShowFormSuccessPopUp } =
    storeToRefs(mainStore);
  const { profile } = storeToRefs(profileStore);
  const { issueTypes, form, formErrorMessage } = storeToRefs(
    technicalSupportStore
  );
  const { privacyPolicyUrl } = useWebsiteUrl();
  const { isCN } = useLanguage();
  const {
    captcha,
    verifyCaptcha,
    expireCaptcha,
    validateCaptcha,
    resetCaptcha,
  } = useCaptcha('technical_support');

  const isSubmitting = ref<boolean>(false);
  const isValidPrivacy = ref<boolean>(true);

  const privacy = ref<IFormPrivacy>({
    japanAgreeSharePersonalData: false,
    turkeyAgreeSharePersonalData: false,
    koreaAgreeAdvPolicy: false,
    koreaAgreePersonalInfo: false,
  });

  const handleUpdatePrivacy = (payload: IFormPrivacy): void => {
    Object.assign(privacy.value, payload);
  };

  const handleValidPrivacy = (payload: boolean): void => {
    isValidPrivacy.value = payload;
  };

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting.value) {
      return;
    }

    const isValidateForm =
      technicalSupportStore.VALID_FORM() && isValidPrivacy.value;

    if (!isValidateForm) {
      isShowFormWarningPopUp.value = true;
      return;
    }

    const isValidateCaptcha = await validateCaptcha();
    if (!isValidateCaptcha) return;

    await postFormData();
  };

  const setUpFormData = (): FormData => {
    const formData = new FormData();

    formData.append('IssueTypeCode', form.value.issueType);
    formData.append('captcha.token', captcha.value.token);
    formData.append('firstName', form.value.firstName);
    formData.append('lastName', form.value.lastName);
    formData.append('emailAddress', form.value.email);
    formData.append('phoneCountryCode', form.value.phoneCode);
    formData.append('phoneNumber', form.value.phoneNumber);
    formData.append('companyName', form.value.companyName);
    formData.append('country', form.value.country);
    formData.append('message', form.value.message);
    formData.append('attachment', form.value.attachment as Blob);
    formData.append('relatedProductName', form.value.relatedProductName);

    // Append utm params
    const utmSource: string = (query?.utm_source as string) || '';
    const utmMedium: string = (query?.utm_medium as string) || '';

    formData.append('utmSource', utmSource);
    formData.append('utmMedium', utmMedium);

    formData.append('websiteUrl', window.location.href);

    return formData;
  };

  const postFormData = async (): Promise<void> => {
    isSubmitting.value = true;
    const formData = setUpFormData();
    try {
      await technicalSupportStore.POST_TECHNICAL_SUPPORT(formData);
      isShowFormSuccessPopUp.value = true;
    } catch (error) {
      console.log(error);
    } finally {
      resetCaptcha();
      isSubmitting.value = false;
    }
  };

  const setDefaultIssueType = (): void => {
    if (issueTypes.value.length && query?.IssueType) {
      const index = Number(query?.IssueType);
      if (isNumber(index)) {
        form.value.issueType = issueTypes.value[index].code || '';
      }
    }
  };

  onBeforeMount(() => {
    technicalSupportStore.RESET_FORM();
    if (profile.value.isLogin) {
      technicalSupportStore.FILL_FORM_WITH_PROFILE();
    }
  });

  onMounted(() => {
    setDefaultIssueType();
  });

  watch(
    () => profile.value.isLogin,
    (value: boolean) => {
      if (value) {
        technicalSupportStore.FILL_FORM_WITH_PROFILE();
      }
    }
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "en-jp": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "en-tw": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "en-kr": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "en-americas": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "en-asia-pacific": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "en-emea": {
    "issue-type": "How may we help",
    "related-product-name": "Which product is this issue related to?",
    "attachment": "Attachment (Please illustrate your issues with related file here)",
    "type-products-technical-support": "Products Technical Support",
    "type-website-support": "Website Support",
    "type-other-technical-support": "Others Technical Support"
  },
  "ja-jp": {
    "issue-type": "お問い合わせ先",
    "related-product-name": "どの製品に関する問題ですか？",
    "attachment": "アタッチメント ( 関連するファイルと共に、あなたの問題をここに記入してください )",
    "type-products-technical-support": "製品のテクニカルサポート",
    "type-website-support": "Webサービスのサポート",
    "type-other-technical-support": "その他"
  },
  "zh-cn": {
    "issue-type": "我们如何提供帮助",
    "related-product-name": "此问题与哪个产品相关？",
    "attachment": "上传附件",
    "type-products-technical-support": "产品技术的支持",
    "type-website-support": "网站服务的支持",
    "type-other-technical-support": "其他技术支持"
  },
  "zh-tw": {
    "issue-type": "我們該如何提供幫助",
    "related-product-name": "此問題與哪個產品相關？",
    "attachment": "上傳附件（請上傳能輔助說明您問題的相關檔案）",
    "type-products-technical-support": "產品技術支援",
    "type-website-support": "網站相關支援",
    "type-other-technical-support": "其他技術支援"
  }
}
</i18n>
